import { MarkAsCloseOrOpenMessageProps, SurrogateMessageProps } from './cache.types';
import {
  ChatMessage,
  DirectionTypeEnum,
  MessageConversation,
  MessageConversationTypeEnum,
} from '@/types/generated/graphql';

let uniqueId = 0;

export function createSurrogateMessage({
  messageContent,
  channelId,
  client,
  agent,
  ...rest
}: Omit<SurrogateMessageProps, 'surrogateMsgCount'>): MessageConversation {
  uniqueId++;

  return {
    __typename: 'MessageConversation',
    id: -uniqueId,
    messageContent,
    direction: DirectionTypeEnum.outgoing,
    conversationType: MessageConversationTypeEnum.message,
    channelId: +channelId,
    createdAt: new Date().toISOString(),
    pinned: false,
    markAsClosed: null,
    durationConference: null,
    conversationStatus: 'delivered',
    recordingUrl: null,
    friendlyName: 'surrogate',
    voicemailRecordingUrl: null,
    forwardedAt: null,
    forwardedTo: null,
    messageConversationErrorLog: null,
    client: {
      ...client,
      __typename: 'Contact',
    },
    agent: {
      callPlatform: agent?.callPlatform ?? null,
      user: {
        id: agent?.id,
        name: agent?.name,
        profilePicture: {
          id: agent?.profilePicture?.id,
          url: agent?.profilePicture?.url,
          __typename: 'Media',
        },
        __typename: 'User',
      },
      userOnlineManagement: {
        isActive: true,
      },
      __typename: 'UserWorkspace',
    },
    callRecording: null,
    voicemailRecording: null,
    markAsBlocked: null,
    ...rest,
  };
}

export function createMarkAsClosedOrOpenMessage({
  markAsClosed,
  surrogateMsgCount,
  channelId,
  client,
  agent,
}: MarkAsCloseOrOpenMessageProps) {
  const uniqueId = surrogateMsgCount + channelId + client.id;

  return {
    __typename: 'MessageConversationEdge',
    id: uniqueId,
    cursor: 'surrogate-' + uniqueId.toString(),
    node: {
      __typename: 'MessageConversation',
      id: uniqueId,
      messageContent: null,
      direction: 'outgoing',
      conversationType: null,
      channelId: channelId,
      updatedAt: new Date().toISOString(),
      pinned: false,
      markAsClosed,
      durationConference: null,
      conversationStatus: 'delivered',
      recordingUrl: null,
      friendlyName: '',
      voicemailRecordingUrl: null,
      forwardedAt: null,
      client: {
        ...client,
        __typename: 'Contact',
      },
      agent: {
        __typename: 'UserWorkspace',
        user: {
          ...agent,
          __typename: 'User',
        },
      },
    },
  };
}

export function createRecentMessageOnSendMessage(messageContent: string, numMedia: number) {
  return {
    updatedAt: new Date().toISOString(),
    unreadCounter: 0,
    messageConversation: {
      id: new Date().toISOString(),
      messageContent,
      conversationStatus: 'delivered',
      conversationType: 'message',
      direction: 'outgoing',
      voicemailRecordingUrl: null,
      pinned: false,
      numMedia,
      forwardedTo: null,
    },
    draft: {
      mediaCount: 0,
      messageContent: '',
    },
  };
}

export function createSurrogateMessageMember({
  surrogateMsgCount,
  messageContent,
  channelId,
  client,
  agent,
}: SurrogateMessageProps): MessageConversation {
  const uniqueId = 666 + surrogateMsgCount;

  return {
    __typename: 'MessageConversation',
    id: uniqueId,
    messageContent,
    direction: DirectionTypeEnum.outgoing,
    conversationType: MessageConversationTypeEnum.message,
    channelId: +channelId,
    createdAt: new Date().toISOString(),
    pinned: false,
    markAsClosed: null,
    durationConference: null,
    conversationStatus: 'delivered',
    recordingUrl: null,
    friendlyName: '',
    voicemailRecordingUrl: null,
    forwardedAt: null,
    client: {
      ...client,
      __typename: 'Contact',
    },
    agent: {
      __typename: 'UserWorkspace',
      user: {
        ...agent,
        __typename: 'User',
      },
    },
  };
}
export function replaceSurrogateMessageMember(responseMessage: ChatMessage) {
  return {
    __typename: 'ChatMessage',
    id: responseMessage?.id,
    ...responseMessage,
  } as ChatMessage;
}
